<script setup lang="ts">
  import { isSameDay, isSameMonth, formatDate } from '@travelata-libs/utils';
  import { computed, toRefs } from 'vue';
  import { CalendarSize, type DateTimestamp } from './Calendar.types';
  import { useCalendar } from './composables/useCalendar';
  import { MS_IN_DAY } from './utils/constants';
  import { dateBetweenMinAndMaxDate } from './utils/date';
  import { Typo, TypoVariant } from '@/components';

  interface CalendarDayProps {
    day: DateTimestamp;
    templateDate: DateTimestamp;
    size?: `${CalendarSize}`;
    range?: boolean;
    mobile?: boolean;
    isToday?: boolean;
    double?: boolean;
    flexibleDays?: number | null;
    minDate?: Date | null;
    maxDate?: Date | null;
  }

  const props = withDefaults(defineProps<CalendarDayProps>(), {
    size: CalendarSize.M,
    range: false,
    mobile: false,
    double: false,
    minDate: null,
    maxDate: null,
    isToday: false,
    flexibleDays: null,
  });

  interface CalendarDayEmits {
    (e: 'click', value: DateTimestamp): void;
    (e: 'hover', value: DateTimestamp): void;
  }

  const emit = defineEmits<CalendarDayEmits>();

  const { day, minDate, maxDate } = toRefs(props);
  const { systemModelValue, displayedDate } = useCalendar();

  const formatedDay = computed(() => formatDate(day.value, 'd'));

  const textVariant = computed(() =>
    props.size === CalendarSize.M ? TypoVariant.BodyMRegular : TypoVariant.BodyLRegular,
  );

  const startDay = computed(() => {
    const modelValue = systemModelValue.value;

    if (!modelValue && !modelValue?.start) return false;

    return modelValue?.start;
  });

  const endDay = computed(() => {
    const modelValue = systemModelValue.value;

    if (!modelValue && !modelValue?.end) return false;

    return modelValue?.end;
  });

  const isStart = computed(() => {
    if (!startDay.value) return false;

    return isSameDay(startDay.value, day.value);
  });

  const isEnd = computed(() => {
    if (!endDay.value) return false;

    return isSameDay(endDay.value, day.value);
  });

  const isHighlighted = computed(() => {
    if (!startDay.value || !endDay.value) return false;

    return day.value >= startDay.value && day.value <= endDay.value;
  });

  const isOtherMonth = computed(() => !isSameMonth(day.value, props.templateDate));
  const isDisabled = computed(() => !dateBetweenMinAndMaxDate(day.value, minDate, maxDate));
  const isFlexible = computed(() => {
    if (!props.flexibleDays || !startDay.value || isDisabled.value || props.range) return false;
    const diffInDays = Math.round((day.value - startDay.value) / MS_IN_DAY);
    return diffInDays !== 0 && Math.abs(diffInDays) <= props.flexibleDays;
  });

  const classList = computed(() => ({
    'tl-calendar-day_disabled': isDisabled.value,
    'tl-calendar-day_highlighted': isHighlighted.value,
    'tl-calendar-day_start': isStart.value,
    'tl-calendar-day_end': isEnd.value,
    'tl-calendar-day_other': isOtherMonth.value && !props.double,
    'tl-calendar-day_hidden': isOtherMonth.value && props.double,
    'tl-calendar-day_bordered': props.isToday,
    'tl-calendar-day_flexible': isFlexible.value,
  }));

  const onClick = () => {
    if (isDisabled.value) return;

    if (isOtherMonth.value && !props.double) {
      displayedDate.value = day.value;
    }

    emit('click', day.value);
  };

  const onMouseEnter = () => {
    if (props.mobile) {
      return;
    }

    emit('hover', day.value);
  };
</script>

<template>
  <div
    :class="['tl-calendar-day', `tl-calendar-day_size-${size}`, classList]"
    :data-day="day"
    @mouseenter="onMouseEnter"
    @click="onClick"
  >
    <div class="tl-calendar-day__content">
      <Typo :variant="textVariant">{{ formatedDay }}</Typo>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .tl-calendar-day {
    $root: &;

    align-items: center;
    color: var(--color-text-primary);
    cursor: pointer;
    display: flex;
    justify-content: center;
    position: relative;
    z-index: 1;

    @include disable-tap-highlight;

    &_size-l {
      #{$root}__content {
        height: 52px;
        width: 52px;
      }
    }

    &_flexible {
      #{$root}__content {
        background-color: rgb(from var(--color-brand-primary) r g b / 0.15);
        color: var(--color-text-primary);
      }
    }

    &_bordered {
      #{$root}__content {
        border-radius: var(--rounding-s);
        outline: 1px solid var(--color-text-tertiary);
      }
    }

    &_disabled {
      #{$root}__content {
        color: var(--color-text-tertiary);
      }

      &:hover {
        cursor: not-allowed;
      }
    }

    &_highlighted {
      background-color: var(--color-sky-10);
    }

    &_start,
    &_end {
      #{$root}__content {
        background-color: var(--color-brand-primary);
        border-radius: var(--rounding-s);
        box-sizing: border-box;
        color: var(--color-text-inverse);
      }
    }

    &_start {
      border-bottom-left-radius: 50%;
      border-top-left-radius: 50%;
    }

    &_end {
      border-bottom-right-radius: 50%;
      border-top-right-radius: 50%;
    }

    &_other {
      color: var(--color-text-tertiary);
    }

    &_hidden {
      opacity: 0;
      pointer-events: none;
    }

    &:hover:not(&_highlighted, &_disabled) {
      #{$root}__content {
        background: var(--color-slate-05);
      }
    }

    &__content {
      align-items: center;
      display: flex;
      height: 40px;
      justify-content: center;
      user-select: none;
      width: 40px;
    }
  }
</style>
